import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { PdfService, InvoiceData } from './pdf.service';
import { NativePrinterBridgeService } from './native-printer-bridge.service';
import { LoggerService } from './logger.service';
import {
  buildEscPosOpenDrawer,
  buildEscPosReceipt,
  escPosToBase64,
} from '../shared/utils/escpos-receipt.util';

/** Outcome of a receipt print attempt. Never thrown for a network failure — the caller decides how to surface it. */
export interface ReceiptPrintResult {
  /** How the receipt was ultimately produced: sent to the network printer, or the PDF fallback. */
  method: 'network' | 'pdf';
  /**
   * Human-readable reason the network printer was not used. Present only when the printer was
   * configured and enabled but the attempt failed (so the PDF fallback was used). Absent for the
   * normal browser path where PDF is expected.
   */
  printerError?: string;
}

/**
 * Decides how a receipt is produced:
 *  - When the configured network printer is reachable through the Android WebView bridge, the
 *    receipt is sent straight to it as ESC/POS.
 *  - Otherwise it falls back to the existing PDF flow ({@link PdfService.printInvoice}), which on
 *    Android opens the system print dialog and in the browser opens a printable PDF.
 *
 * Reachability is probed once at app load (and cached) so the "Print Receipt" tap is instant; it
 * is re-probed lazily if the cached result is stale or any.
 */
@Injectable({ providedIn: 'root' })
export class ReceiptPrinterService {
  private lastProbeAt = 0;
  private printerReachable = false;
  private inFlightProbe: Promise<boolean> | null = null;
  /** Serializes ESC/POS writes so drawer kicks and receipts cannot interleave on the wire. */
  private writeChain: Promise<void> = Promise.resolve();

  /** Cached reachability is considered fresh for this long before a re-probe. */
  private static readonly PROBE_TTL_MS = 60_000;

  constructor(
    private readonly pdfService: PdfService,
    private readonly nativeBridge: NativePrinterBridgeService,
    private readonly logger: LoggerService
  ) {}

  /** Whether direct-to-printer receipts are possible right now (config + bridge + last probe). */
  get canPrintDirect(): boolean {
    return (
      environment.printer.enabled && this.nativeBridge.isBridgeAvailable && this.printerReachable
    );
  }

  /**
   * Probes the configured printer and caches the result. Safe to call on app load; never throws.
   * Returns `false` immediately when printing is disabled or the native bridge is absent.
   */
  async refreshAvailability(force = false): Promise<boolean> {
    if (!environment.printer.enabled || !this.nativeBridge.isBridgeAvailable) {
      this.printerReachable = false;
      return false;
    }
    const fresh = Date.now() - this.lastProbeAt < ReceiptPrinterService.PROBE_TTL_MS;
    if (!force && fresh) {
      return this.printerReachable;
    }
    if (this.inFlightProbe) {
      return this.inFlightProbe;
    }
    const { host, port, connectTimeoutMs } = environment.printer;
    this.inFlightProbe = this.nativeBridge
      .checkPrinter(host, port, connectTimeoutMs)
      .then(reachable => {
        this.printerReachable = reachable;
        this.lastProbeAt = Date.now();
        this.logger.info(
          `Receipt printer ${reachable ? 'reachable' : 'not reachable'} at ${host}:${port}`,
          undefined,
          'ReceiptPrinterService'
        );
        return reachable;
      })
      .catch(() => {
        this.printerReachable = false;
        this.lastProbeAt = Date.now();
        return false;
      })
      .finally(() => {
        this.inFlightProbe = null;
      });
    return this.inFlightProbe;
  }

  /**
   * Pulses the cash drawer via the network printer (ESC/POS kick only — no receipt).
   * No-ops when the printer is disabled, the native bridge is absent, or the printer is
   * unreachable. Never throws; failures are logged only so Cash selection is not blocked.
   */
  async openCashDrawer(): Promise<boolean> {
    const { enabled, host, port, connectTimeoutMs } = environment.printer;
    if (!enabled || !this.nativeBridge.isBridgeAvailable) {
      return false;
    }
    const reachable = await this.refreshAvailability();
    if (!reachable) {
      return false;
    }
    try {
      const payload = buildEscPosOpenDrawer();
      await this.enqueueWrite(() =>
        this.nativeBridge.printEscPos(escPosToBase64(payload), host, port, connectTimeoutMs)
      );
      return true;
    } catch (err) {
      this.printerReachable = false;
      this.lastProbeAt = 0;
      this.logger.trackError('ReceiptPrinterService.openCashDrawer', err, { host, port });
      return false;
    }
  }

  /**
   * Prints a receipt for the given invoice. Attempts the network printer first (when reachable)
   * and transparently falls back to the PDF path on any failure.
   * Receipts never open the cash drawer — that happens on Cash payment selection via
   * {@link openCashDrawer}.
   *
   * Resolves with a {@link ReceiptPrintResult} describing how the receipt was produced and, when
   * the network printer was attempted but failed, a human-readable `printerError`. Rejects only
   * when even the PDF fallback fails, with a message suitable for display to the user.
   */
  async printReceipt(invoiceData: InvoiceData): Promise<ReceiptPrintResult> {
    const { enabled, host, port, charsPerLine, connectTimeoutMs } = environment.printer;
    let printerError: string | undefined;

    if (enabled && this.nativeBridge.isBridgeAvailable) {
      const reachable = await this.refreshAvailability();
      if (reachable) {
        try {
          const payload = buildEscPosReceipt(invoiceData, charsPerLine);
          await this.enqueueWrite(() =>
            this.nativeBridge.printEscPos(escPosToBase64(payload), host, port, connectTimeoutMs)
          );
          return { method: 'network' };
        } catch (err) {
          // Printer became unreachable / write failed — invalidate cache and fall back.
          this.printerReachable = false;
          this.lastProbeAt = 0;
          printerError = ReceiptPrinterService.describePrinterError(err);
          this.logger.trackError('ReceiptPrinterService.printReceipt', err, {
            host,
            port,
          });
        }
      } else {
        printerError = 'The receipt printer is not reachable on the network.';
      }
    }

    try {
      const opened = this.pdfService.printInvoice(invoiceData);
      if (!opened) {
        throw new Error(
          'The receipt window was blocked. Please allow pop-ups for this site and try again.'
        );
      }
      return { method: 'pdf', printerError };
    } catch (err) {
      this.logger.trackError('ReceiptPrinterService.printReceipt.pdf', err, { host, port });
      const reason =
        err instanceof Error && err.message
          ? err.message
          : 'There was an error generating the receipt. Please try again.';
      throw new Error(reason);
    }
  }

  /** Runs `fn` after any in-flight ESC/POS write finishes; failures do not block the queue. */
  private enqueueWrite<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.writeChain.then(fn, fn);
    this.writeChain = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  /** Maps a raw printer/bridge error to a concise, user-facing explanation. */
  private static describePrinterError(err: any): string {
    const raw = err instanceof Error ? err.message : String(err ?? '');
    const msg = raw.toLowerCase();
    if (msg.includes('timed out') || msg.includes('timeout')) {
      return 'The printer did not respond in time. Check that it is powered on and connected to the network.';
    }
    if (msg.includes('not allowed') || msg.includes('security')) {
      return 'The configured printer address is not permitted.';
    }
    if (msg.includes('unavailable') || msg.includes('bridge')) {
      return 'Printing is not available on this device.';
    }
    if (msg.includes('reach') || msg.includes('connect') || msg.includes('refused')) {
      return 'Could not connect to the printer. Check its power and network connection.';
    }
    return raw || 'The printer reported an error.';
  }
}
